<template>
  <div
    v-if="isRendered"
    class="request-inspection-dialog"
    :class="{ 'request-inspection-dialog--open': isShown }"
    role="dialog"
    aria-modal="true"
  >
    <div class="request-inspection-dialog__backdrop" @click.self="close">
      <div
        class="request-inspection-dialog__panel"
        :style="{ width: '700px' }"
        @click.stop
      >
        <!-- Background Pattern Overlay -->
        <div
          class="request-inspection-dialog__background-pattern-overlay"
        ></div>

        <!-- Header with icon and close button (like ExActionsPopUp) -->
        <div class="request-inspection-dialog__header">
          <div class="request-inspection-dialog__icon-button">
            <Icon name="interface_search" />
          </div>
          <v-spacer />
          <span class="request-inspection-dialog__close-icon" @click="close">
            <v-btn icon color="grey">
              <Icon class="cursor-pointer" name="popup_close"></Icon>
            </v-btn>
          </span>
        </div>

        <!-- Content - all centered -->
        <div class="request-inspection-dialog__content">
          <!-- Title -->
          <h3 class="request-inspection-dialog__title">Request inspection</h3>

          <!-- Introductory Text -->
          <p class="request-inspection-dialog__intro-text">
            You are about to request an inspection to prove your readiness to
            fulfill the responsibility {{ responsibilityName }}.
          </p>

          <!-- Readiness Level Picker -->
          <div class="request-inspection-dialog__field">
            <BadgeLevelPicker
              :modelValue="readinessLevel"
              :levels="levels"
              @update:modelValue="onReadinessLevelChange"
            />
          </div>

          <!-- Available Inspectors Section -->
          <div
            v-if="hasInspectorsValue && inspectors.length > 0"
            class="request-inspection-dialog__field"
          >
            <EmployeePicker
              label="Inspector"
              placeholder="Select an inspector"
              :employeeList="transformedInspectors"
              :disable="false"
              @emp-selected="onInspectorSelected"
            />
          </div>

          <!-- No Inspectors Available Message -->
          <div
            v-if="showNoInspectorsWarning"
            class="request-inspection-dialog__notification-warning"
            role="alert"
          >
            <v-img
              class="request-inspection-dialog__notify-icon"
              :src="require('@components/assets/erroriconoutline.svg')"
              contain
            />
            <div class="request-inspection-dialog__text-content">
              <span class="request-inspection-dialog__main-text"
                >No coach is currently available for this Responsibility.</span
              >
              <span class="request-inspection-dialog__sub-text"
                >Please contact your administrator.</span
              >
            </div>
          </div>

          <!-- Expedited Inspection Checkbox -->
          <div
            :class="`request-inspection-dialog__checkbox-field ${
              showNoInspectorsWarning
                ? 'request-inspection-dialog__checkbox-field--without-inspectors'
                : ''
            }`"
          >
            <CheckboxInput
              v-model="expedited"
              label="Request expedited inspection"
            />
            <span class="request-inspection-dialog__checkbox-helper">
              This request will show as expedited to inspectors.
            </span>
          </div>
        </div>

        <div class="request-inspection-dialog__footer">
          <StandardButton
            type="magenta"
            variant="primary"
            size="full-width"
            :label="'Make Request'"
            :loaderLeft="$loader.isAppLoading"
            :disabled="$loader.isAppLoading || !canRequest"
            @click="handleMakeRequest"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import BadgeLevelPicker from "@components/BadgeLevelPicker/BadgeLevelPicker.vue";
import EmployeePicker from "@components/EmployeePicker/ExEmployeePicker.vue";

const ANIMATION_MS = 180;

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  responsibilityName: {
    type: String,
    default: "Communicate Internally",
  },
  has_inspectors: {
    type: Boolean,
    default: true,
  },
  /** CamelCase alias so :has-inspectors from parent binds correctly (Vue maps kebab to camel) */
  hasInspectors: {
    type: Boolean,
    default: undefined,
  },
  inspectors: {
    type: Array,
    default: () => [],
  },
  /** Readiness levels to offer in the BadgeLevelPicker. Defaults to the picker's all levels. */
  levels: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(["input", "onRequest", "onClose"]);

/** Use hasInspectors if set (from :has-inspectors binding), else has_inspectors */
const hasInspectorsValue = computed(() => {
  if (props.hasInspectors !== undefined) return props.hasInspectors;
  return props.has_inspectors;
});

/** Show the "no inspectors available" warning when flag says no inspectors OR list is empty */
const showNoInspectorsWarning = computed(() => {
  return !hasInspectorsValue.value || props.inspectors.length === 0;
});

/** Rule 3: a coach must be chosen, and none can be chosen when no coach is available */
const canRequest = computed(
  () => !showNoInspectorsWarning.value && selectedInspector.value != null
);

// Form data
const expedited = ref(false);
const readinessLevel = ref("apprentice");
const selectedInspector = ref(null);

// Always keep an internal open state so the dialog can visually close itself
const internalOpen = ref(props.value);
watch(
  () => props.value,
  (val) => {
    internalOpen.value = val;
    // Reset form when dialog opens
    if (val) {
      expedited.value = false;
      readinessLevel.value = "apprentice";
      selectedInspector.value = null;
    }
  },
  { immediate: true }
);

// We keep the dialog mounted briefly on close so CSS can animate out.
const isRendered = ref(props.value);
const isShown = ref(props.value);
let closeTimer = null;

const syncAnimationState = (open) => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  if (open) {
    isRendered.value = true;
    requestAnimationFrame(() => {
      isShown.value = true;
    });
  } else {
    isShown.value = false;
    closeTimer = setTimeout(() => {
      if (!internalOpen.value) isRendered.value = false;
      closeTimer = null;
    }, ANIMATION_MS);
  }
};

watch(
  internalOpen,
  (open) => {
    syncAnimationState(open);
  },
  { immediate: true }
);

const close = () => {
  internalOpen.value = false;
  emit("input", false);
  emit("onClose");
};

const onReadinessLevelChange = (level) => {
  readinessLevel.value = level;
};

const onInspectorSelected = (inspector) => {
  selectedInspector.value = inspector;
};

const handleMakeRequest = () => {
  if (!canRequest.value) return;
  emit("onRequest", {
    expedited: expedited.value,
    readinessLevel: readinessLevel.value,
    inspector: selectedInspector.value,
    inspectorId: selectedInspector.value.id,
  });
};

// Transform inspectors data to the EmployeePicker shape
const transformedInspectors = computed(() => {
  return props.inspectors.map((inspector, index) => {
    const name = inspector.name || inspector.fullName || "";
    const firstName =
      inspector.firstName || (name ? name.split(" ")[0] : "") || "";
    const lastName =
      inspector.lastName || (name ? name.split(" ").slice(1).join(" ") : "") ||
      "";
    const fullName =
      inspector.fullName || `${firstName} ${lastName}`.trim();

    return {
      id: inspector.id ?? index,
      fullName,
      firstName,
      lastName,
      title: inspector.title || "",
      avatar: inspector.avatar || inspector.img || "",
    };
  });
});

const onKeydown = (e) => {
  if (e.key === "Escape") {
    close();
  }
};

let previousBodyOverflow = null;
watch(
  isRendered,
  (rendered) => {
    if (rendered) {
      document.addEventListener("keydown", onKeydown);
      previousBodyOverflow = document?.body?.style?.overflow ?? "";
      if (document?.body) document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("keydown", onKeydown);
      if (document?.body && previousBodyOverflow !== null) {
        document.body.style.overflow = previousBodyOverflow;
      }
      previousBodyOverflow = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  document.removeEventListener("keydown", onKeydown);
  if (document?.body && previousBodyOverflow !== null) {
    document.body.style.overflow = previousBodyOverflow;
  }
});
</script>

<style scoped>
.request-inspection-dialog {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.request-inspection-dialog__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  opacity: 0;
  transition: opacity 180ms ease;
  pointer-events: none;
}

.request-inspection-dialog--open {
  pointer-events: auto;
}

.request-inspection-dialog--open .request-inspection-dialog__backdrop {
  opacity: 1;
  pointer-events: auto;
}

.request-inspection-dialog__panel {
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(16, 24, 40, 0.2);
  transform: translateY(10px) scale(0.98);
  opacity: 0;
  transition: transform 180ms ease, opacity 180ms ease;
  will-change: transform, opacity;
  position: relative;
  overflow: hidden;
}

.request-inspection-dialog--open .request-inspection-dialog__panel {
  transform: translateY(0) scale(1);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .request-inspection-dialog__backdrop,
  .request-inspection-dialog__panel {
    transition: none !important;
  }
}

.request-inspection-dialog__background-pattern-overlay {
  position: absolute;
  top: 68px;
  left: 50%;
  transform: translateX(-50%);
  width: 216px;
  height: 216px;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0.5)
    ),
    url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: contain;
  background-repeat: no-repeat;
  pointer-events: none;
  z-index: 0;
}

.request-inspection-dialog__header {
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 24px;
  z-index: 1;
}

.request-inspection-dialog__icon-button {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  cursor: pointer;
}

.request-inspection-dialog__close-icon {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}

.request-inspection-dialog__close-icon .v-btn {
  margin: 0 !important;
}

.request-inspection-dialog__cursor-pointer {
  cursor: pointer;
}

.request-inspection-dialog__content {
  margin-bottom: 24px;
  text-align: center;
  position: relative;
  z-index: 1;
}

.request-inspection-dialog__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
  margin: 0 0 20px 0;
  text-align: center;
}

.request-inspection-dialog__intro-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
  margin: 0 0 20px 0;
  text-align: center;
}

/* Form fields (readiness level + inspector) */
.request-inspection-dialog__field {
  max-width: 440px;
  margin: 0 auto 24px auto;
  text-align: left;
}

/* Checkbox Field */
.request-inspection-dialog__checkbox-field {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}
.request-inspection-dialog__checkbox-field--without-inspectors {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  max-width: 550px;
  margin: 0 auto;
}

.request-inspection-dialog__checkbox-helper {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  position: relative;
  top: -10px;
  left: 38px;
}

.request-inspection-dialog__notification-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 592px;
  margin: 0 auto 24px auto;
  justify-content: flex-start;
  padding: 12px 16px;
}

.request-inspection-dialog__notify-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.request-inspection-dialog__text-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.request-inspection-dialog__main-text {
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #344054;
}

.request-inspection-dialog__sub-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #475467;
}

.request-inspection-dialog__footer {
  display: flex;
  margin-top: 24px;
}

.request-inspection-dialog__button {
  width: 100%;
}
</style>
